/*
Para usar o componente de Seleção Básica importe-o. No seu uso coloque na variável data o arrei de objetos que será usado na seleção, 
em nameBD fica o que será enviado ao banco de dados, já em descrição fica o que será apresentado ao usuário, na variável label fica o nome 
do input que apareçe para o usuário e na variável name fica a referencia da variável que receberá o valor da seleção, no exemplo abaixo 
o valor selecionado será armazenado na variável name e repassado para a variável estadocivil.

import { BasicSelect } from '../../../shared/forms/BasicSelect';

<SelectNumber
  data={[
    { nameBD: 1, descricao: 'Casado (a)' },
    { nameBD: 2, descricao: 'Solteiro (a)' },
    { nameBD: 3, descricao: 'Divorciado (a)' },
  ]}
  disabled={isLoading}
  label='Estado Civil'
  name='estadocivil'
/>
                
*/
import * as React from 'react';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { useField } from '@unform/core';
import { useEffect } from 'react';
import { alpha, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

interface IDadosSelect {
  nameBD: number;
  descricao: string;
}

interface BasicSelectProps {
  data: IDadosSelect[];
  label: string;
  name: string;
  disabled: boolean; 
  valor?: (novoTextoselect: string | undefined) => void;
}

// Interface extendida para incluir muiName
type StyledSelectProps = SelectProps<unknown> & {
  muiName?: string;
};

// Componente estilizado com bordas arredondadas - Fundo preto no modo escuro
const StyledSelect = styled(Select)<StyledSelectProps>(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha('#000000', 0.8) // Fundo preto com transparência
      : alpha(theme.palette.common.white, 0.8),
    transition: 'all 0.3s ease',
    
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha('#000000', 0.9) // Fundo preto mais opaco no hover
        : alpha(theme.palette.common.white, 0.9),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha('#000000', 0.95) // Fundo preto quase sólido no focus
        : alpha(theme.palette.common.white, 0.95),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
  },
}));

// Adicionar a propriedade muiName de forma type-safe
Object.assign(StyledSelect, { muiName: 'Select' });

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  borderRadius: '8px',
  margin: '4px 8px',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.2)
      : alpha(theme.palette.primary.main, 0.1),
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.3)
      : alpha(theme.palette.primary.main, 0.2),
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.primary.main, 0.4)
        : alpha(theme.palette.primary.main, 0.3),
    },
  },
}));

// InputLabel estilizado para centralizar corretamente
const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  '&.Mui-focused': {
    color: theme.palette.primary.dark,
  },
}));

export const SelectNumber: React.FC<BasicSelectProps> = (props) => {
  const [age, setAge] = React.useState('');
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    clearError(); 
    setAge(event.target.value as string);
    props.valor?.(event.target.value as string);
  };

  const { fieldName, registerField, error, clearError } = useField(props.name);

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => age,
      setValue: (_, newValue) => setAge(newValue),
    });
  }, [registerField, fieldName, age]);

  return (
    <Box sx={{ minWidth: 30 }}>
      <FormControl fullWidth error={!!error}>
        <StyledInputLabel id="demo-simple-select-label">
          {props.label}
        </StyledInputLabel>
        <StyledSelect
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={age}
          label={props.label}
          onChange={handleChange}
          disabled={props.disabled}
          size='small'
          sx={{
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha('#000000', 0.8) // Fundo preto com transparência
                : alpha(theme.palette.common.white, 0.8),
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha('#000000', 0.9) // Fundo preto mais opaco no hover
                  : alpha(theme.palette.common.white, 0.9),
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              '&.Mui-focused': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha('#000000', 0.95) // Fundo preto quase sólido no focus
                  : alpha(theme.palette.common.white, 0.95),
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: '12px',
                marginTop: '8px',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? '#1E1E1E' // Fundo escuro para o dropdown
                  : theme.palette.common.white,
                color: theme.palette.text.primary,
                boxShadow: theme.shadows[4],
                
                '& .MuiMenuItem-root': {
                  borderRadius: '8px',
                  margin: '4px 8px',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.primary.main, 0.2)
                      : alpha(theme.palette.primary.main, 0.1),
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.primary.main, 0.3)
                      : alpha(theme.palette.primary.main, 0.2),
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.primary.main, 0.4)
                        : alpha(theme.palette.primary.main, 0.3),
                    },
                  },
                },
              },
            },
          }}
        >
          {props.data.map((item) => (
            <StyledMenuItem key={item.nameBD} value={item.nameBD}>
              {item.descricao}
            </StyledMenuItem>
          ))}
        </StyledSelect>
      </FormControl>
      {error && <div style={{ fontSize: 14, color: '#CF2330', marginTop: '4px' }}>{error}</div>} 
    </Box>
  );
};